import { contextStore } from "./context-store.js";
import { platform } from "../../app/browser-platform.js";

export interface AuthorizedClientSummary {
  id: string;
  name: string;
}

export type TenantResolutionStatus = "RESOLVED" | "NO_AUTHORIZED_CLIENT" | "TENANT_RESOLUTION_ERROR";

export interface TenantResolutionResult {
  status: TenantResolutionStatus;
  authorizedClients: AuthorizedClientSummary[];
  activeClientId: string | null;
  activeClientName: string;
  error?: string;
}

/**
 * Resolves the authorized clients for the currently authenticated user
 * and ensures an active client context is set in `contextStore`.
 *
 * Rules:
 * 1. Queries `platform.adminService.listAuthorizedClients(ctx)`.
 * 2. If query throws/fails (ERROR):
 *    - Status is TENANT_RESOLUTION_ERROR.
 *    - If contextStore already had an activeClientId, it is PRESERVED (never cleared on temporary error).
 *    - If no prior activeClientId, activeClientId remains null.
 *    - Never treats an error as an empty list (ERROR != []).
 * 3. If query succeeds with 0 authorized clients:
 *    - Status is NO_AUTHORIZED_CLIENT.
 *    - Clears activeClientId and activeClientName in contextStore.
 * 4. If query succeeds with >0 authorized clients:
 *    - Status is RESOLVED.
 *    - If contextStore already has an activeClientId AND it exists in the authorized list:
 *      - Keeps that activeClientId, ensures activeClientName is up-to-date.
 *    - Else:
 *      - Selects the first authorized client by default and persists in contextStore.
 */
export async function ensureActiveClientContext(): Promise<TenantResolutionResult> {
  const currentCtx = contextStore.get();
  if (!currentCtx.isAuthenticated || !currentCtx.userId) {
    return {
      status: "NO_AUTHORIZED_CLIENT",
      authorizedClients: [],
      activeClientId: null,
      activeClientName: ""
    };
  }

  let authorizedClients: AuthorizedClientSummary[] = [];
  let queryFailed = false;
  let queryError = "";

  try {
    if (platform && platform.adminService) {
      const requestContext = {
        activeClientId: undefined,
        permissions: currentCtx.permissions,
        correlationId: "",
        userId: currentCtx.userId,
        professionalId: currentCtx.professionalId || null
      } as any;

      const clients = await platform.adminService.listAuthorizedClients(requestContext);
      if (Array.isArray(clients)) {
        authorizedClients = clients
          .filter((c: any) => c && c.id && c.status === "ACTIVE" && c.deletedAt === null)
          .map((c: any) => ({
            id: String(c.id),
            name: String(c.name || c.tradeName || c.corporateReason || c.id)
          }));
      }
    }
  } catch (err: any) {
    queryFailed = true;
    queryError = err?.safeMessage || err?.message || "Erro ao consultar clientes autorizados.";
    console.warn("[TENANT] Could not list authorized clients:", err);
  }

  if (queryFailed) {
    // Preserve existing valid client context if present
    if (currentCtx.activeClientId) {
      return {
        status: "TENANT_RESOLUTION_ERROR",
        authorizedClients: [],
        activeClientId: currentCtx.activeClientId,
        activeClientName: currentCtx.activeClientName || "",
        error: queryError
      };
    }
    return {
      status: "TENANT_RESOLUTION_ERROR",
      authorizedClients: [],
      activeClientId: null,
      activeClientName: "",
      error: queryError
    };
  }

  if (authorizedClients.length === 0) {
    if (currentCtx.activeClientId) {
      contextStore.set({ activeClientId: null, activeClientName: "" });
    }
    return {
      status: "NO_AUTHORIZED_CLIENT",
      authorizedClients: [],
      activeClientId: null,
      activeClientName: ""
    };
  }

  // Check if current selection is valid
  const currentId = currentCtx.activeClientId;
  const match = authorizedClients.find(c => c.id === currentId);

  let selectedId: string;
  let selectedName: string;

  if (match) {
    selectedId = match.id;
    selectedName = match.name;
  } else {
    // Default to the first available authorized client
    selectedId = authorizedClients[0]!.id;
    selectedName = authorizedClients[0]!.name;
  }

  if (currentCtx.activeClientId !== selectedId || currentCtx.activeClientName !== selectedName) {
    contextStore.set({
      activeClientId: selectedId,
      activeClientName: selectedName
    });
  }

  return {
    status: "RESOLVED",
    authorizedClients,
    activeClientId: selectedId,
    activeClientName: selectedName
  };
}
